import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface ManagementDeviceType {
  type:
    | "building_settlement"
    | "ground_settlement"
    | "ground_water"
    | "utility_settlement"
    | "tilting"
    | "vibration"
    | "crack"
    | "movement"
    | "ground_movement"
    | "building_movement"
    | "utility_movement";
  deviceCount: number;
  maxValue: number | null;
  latestReadingDate: string | null;
  aaaExceedances: {
    alert: number;
    alarm: number;
    action1: number;
    action2: number;
    action3: number;
  };
  waterLevelExceedances: {
    upperBound: number;
    lowerBound: number;
  } | null;
  damageReports: number;
}

export interface ManagementDashboardData {
  deviceTypes: ManagementDeviceType[];
  totalDevices: number;
  lastUpdated: string;
}

export interface SiteLayoutDevicePosition {
  id: string;
  name: string;
  type: string;
  position: { x: number; y: number } | null;
  coordinates: { northing: number; easting: number };
  alertStatus: "normal" | "alert" | "alarm" | "action1" | "action2" | "action3";
  thresholds:
    | {
        alert: number | null;
        alarm: number | null;
        action1: number | null;
        action2: number | null;
        action3: number | null;
      }
    | { upperBound: number | null; lowerBound: number | null };
  recentReading: { measurementAt: string; value: number } | null;
}

export interface SiteLayoutCalibrationPoint {
  id: number;
  layout_id: number;
  image_x: number;
  image_y: number;
  real_northing: number;
  real_easting: number;
}

export interface SiteLayoutItem {
  id: number | null;
  name: string;
  imageFilename: string;
  sortOrder: number;
  calibrationPoints: SiteLayoutCalibrationPoint[];
}

export interface SiteLayoutData {
  project: { id: number; name: string; layoutImage: string };
  layouts: SiteLayoutItem[];
  devices: SiteLayoutDevicePosition[];
}

export const managementDashboardApi = createApi({
  reducerPath: "managementDashboardApi",
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL }),
  tagTypes: ["ManagementDashboard", "SiteLayout"],
  endpoints: (builder) => ({
    getManagementDashboard: builder.query<
      ManagementDashboardData,
      { projectId?: number; date?: string }
    >({
      query: (params) => {
        const sp = new URLSearchParams();
        if (params.projectId != null)
          sp.append("projectId", String(params.projectId));
        if (params.date) sp.append("date", params.date);
        return {
          url: `daily-readings/management-dashboard?${sp.toString()}`,
        };
      },
      providesTags: ["ManagementDashboard"],
    }),
    getSiteLayout: builder.query<SiteLayoutData, number>({
      query: (projectId) => ({
        url: `daily-readings/site-layout/${projectId}`,
      }),
      providesTags: ["SiteLayout"],
    }),
  }),
});

export const { useGetManagementDashboardQuery, useGetSiteLayoutQuery } =
  managementDashboardApi;
